import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, BUTTON_THEME } from '@st-operations/uikit/src/v2'

import { STATUS_CLASSES, STATUS_LABELS, actionOf, sinceHeartbeat } from './constants'
import { downloadResults } from './download-results'

// One row per push. A push that is still going shows its progress here rather
// than making anyone open it, so "is anything running?" is answerable at a
// glance.
//
// Shared by a taxonomy's list of pushes and the list of every push, which
// adds a column for the taxonomy each is for (`showTaxonomy`). `linkTo` gives
// a push's page - each list opens pushes under its own address, so Back
// returns to it. A push that has run can have its results downloaded from its
// row, the same file as from its page.
const PushTable = ({ pushes, partners, linkTo, showTaxonomy }) => {
  // the push whose results are on their way, and the last one that failed
  const [downloading, setDownloading] = useState(null)
  const [failed, setFailed] = useState(null)

  const partnerOf = (push) => partners.find((p) => p.slug === push.partner) || null

  const download = async (push) => {
    setDownloading(push._id)
    setFailed(null)
    try {
      await downloadResults(push)
    } catch (e) {
      setFailed({ _id: push._id, message: e?.message || 'could not download the results' })
    }
    setDownloading(null)
  }
  const rowClass = ['push-row', showTaxonomy && 'push-row--taxonomy'].filter(Boolean).join(' ')

  return (
    <div>
      <div className={`${rowClass} font-semibold text-sm text-gray-500`}>
        <If condition={showTaxonomy}>
          <div>Taxonomy</div>
        </If>
        <div>Partner / mapping ID</div>
        <div>Status</div>
        <div>Created</div>
        <div>Ran</div>
        <div>Results</div>
        <div />
      </div>

      <For each="push" of={pushes}>
        <div className={rowClass} key={push._id}>
          <If condition={showTaxonomy}>
            <div className="text-sm">
              {push.taxonomy?.name || <span className="text-gray-500">No taxonomy</span>}
            </div>
          </If>

          <div>
            <div className="font-medium">{partnerOf(push)?.name || push.partner}</div>
            <div className="text-sm text-gray-500">
              {push.mapping_id} · {actionOf(push, partnerOf(push)).label}
            </div>
          </div>

          <div>
            <span className={`status-pill ${STATUS_CLASSES[push.status] || ''}`}>
              {STATUS_LABELS[push.status] || push.status}
            </span>
            <If condition={push.mocked}>
              <div className="text-xs text-amber-700 mt-1">mocked</div>
            </If>
          </div>

          <div className="text-sm">
            {push.created_at ? new Date(push.created_at).toLocaleString() : '-'}
          </div>

          <div className="text-sm">
            <Choose>
              <When condition={push.status === 'running' && push.waiting_for}>
                Waiting its turn
                <div className="text-xs text-gray-500">
                  behind {push.waiting_for.taxonomy || 'another push'}
                </div>
              </When>
              <When condition={push.status === 'running'}>
                {push.position || 0} of {push.counts?.total || '?'}
                <div className="text-xs text-gray-500">
                  last activity {sinceHeartbeat(push) || 'unknown'}
                </div>
              </When>
              <When condition={push.started_at}>{new Date(push.started_at).toLocaleString()}</When>
              <Otherwise>-</Otherwise>
            </Choose>
          </div>

          <div className="text-sm">
            <Choose>
              <When condition={push.status === 'draft'}>
                <span className="text-gray-400">not run</span>
              </When>
              <Otherwise>
                {push.counts?.created || 0} created
                {' · '}
                {push.counts?.updated || 0} updated
                {' · '}
                <If condition={push.counts?.already_there}>
                  {push.counts.already_there} already there
                  {' · '}
                </If>
                <If condition={push.counts?.rate_set}>
                  {push.counts.rate_set} rate set
                  {' · '}
                </If>
                <span className={push.counts?.failed ? 'text-red-600 font-medium' : ''}>
                  {push.counts?.failed || 0} failed
                </span>
                {' · '}
                {push.counts?.skipped || 0} skipped
              </Otherwise>
            </Choose>
          </div>

          <div>
            <div className="push-row__actions">
              <If condition={push.status !== 'draft'}>
                <Button
                  theme={BUTTON_THEME.SECONDARY}
                  onClick={() => download(push)}
                  disabled={downloading === push._id}
                >
                  Download CSV
                </Button>
              </If>
              <Link to={linkTo(push)}>
                <Button theme={BUTTON_THEME.SECONDARY}>Open</Button>
              </Link>
            </div>
            <If condition={failed?._id === push._id}>
              <div className="text-xs text-red-600 mt-1">{failed.message}</div>
            </If>
          </div>
        </div>
      </For>
    </div>
  )
}

export default PushTable
